import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ruRU } from '@mui/material/locale';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/ru';
import { AuthProvider } from './contexts/AuthContext';

import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Vaccination from './pages/Vaccination';
import EmergencyCare from './pages/EmergencyCare';
import ChildrenUpToYear from './pages/ChildrenUpToYear';
import Calculator from './pages/Calculator';
import AIAssistant from './pages/AIAssistant';
import Orders from './pages/Orders';
import Atlas from './pages/Atlas';
import AboutData from './pages/AboutData';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SecurityCompliance from './pages/SecurityCompliance';
import TestingPage from './pages/TestingPage';
import ClinicalRecommendations from './pages/ClinicalRecommendations';
import SystemSettings from './pages/SystemSettings';

const theme = createTheme({
  palette: {
    primary: {
      main: '#be185d', // Бордовый
      light: '#f9a8d4',
      dark: '#831843',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#22c55e', // Зеленый
      light: '#86efac',
      dark: '#15803d',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#ffffff', // Белый
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937', // Темно-серый
      secondary: '#6b7280', // Средне-серый
    },
    grey: {
      50: '#F8F9FA',
      100: '#E9ECEF',
      200: '#DEE2E6',
      300: '#CED4DA',
      400: '#ADB5BD',
      500: '#6C757D',
      600: '#495057',
      700: '#343A40',
      800: '#212529',
      900: '#1A1D20',
    },
    success: {
      main: '#6B8E6B',
      light: '#9BB39B',
      dark: '#4A6B4A',
    },
    warning: {
      main: '#D4A574',
      light: '#E6C19A',
      dark: '#B8935A',
    },
    error: {
      main: '#C17A7A',
      light: '#D49A9A',
      dark: '#A85A5A',
    },
    info: {
      main: '#5B7C99',
      light: '#8BA3C7',
      dark: '#3A5A73',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      color: '#1f2937',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 600,
      color: '#1f2937',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#1f2937',
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#1f2937',
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#1f2937',
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#1f2937',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#1f2937',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#6b7280',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: '#6b7280',
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          padding: '10px 20px',
          fontSize: '0.875rem',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 600,
          fontSize: '0.75rem',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#be185d',
              },
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid #e5e7eb',
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #e5e7eb',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 12px',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            backgroundColor: '#f9fafb',
            transform: 'translateX(6px)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 12px',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            backgroundColor: '#f9fafb',
            transform: 'translateX(6px)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e5e7eb',
          transition: 'all 0.3s ease-in-out',
        },
        indicator: {
          backgroundColor: '#be185d',
          height: 3,
          borderRadius: '2px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 48,
          transition: 'all 0.3s ease-in-out',
          '&.Mui-selected': {
            color: '#be185d',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: '0.875rem',
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#e5e7eb',
          height: 6,
        },
        bar: {
          borderRadius: 8,
        },
      },
    },
  },
}, ruRU);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <AuthProvider>
          <Router>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/vaccination" element={<Vaccination />} />
                <Route path="/emergency" element={<EmergencyCare />} />
                <Route path="/children-up-to-year" element={<ChildrenUpToYear />} />
                <Route path="/atlas" element={<Atlas />} />
                <Route path="/calculator" element={<Calculator />} />
                <Route path="/ai-assistant" element={<AIAssistant />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/clinical-recommendations" element={<ClinicalRecommendations />} />
                <Route path="/system-settings" element={<SystemSettings />} />
              </Routes>
            </Layout>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;